import React from 'react';

/**
 * FrameworkProgressBadge - Shows framework progress inline in chat
 * 
 * Displays:
 * - Framework name
 * - Current stage / total stages
 * - Progress bar
 * - Current stage title
 */
const FrameworkProgressBadge = ({ 
  frameworkName,
  currentStage,
  totalStages,
  currentStageTitle,
  stages,
  isCompleted
}) => {
  const progressPercent = isCompleted ? 100 : Math.round((currentStage / totalStages) * 100);
  
  return (
    <div className={`framework-progress-badge ${isCompleted ? 'completed' : ''}`}>
      <div className="progress-header">
        <span className="framework-icon">{isCompleted ? '✅' : '📊'}</span>
        <span className="framework-name">{frameworkName}</span>
        {isCompleted ? (
          <span className="completed-badge">Complete!</span>
        ) : (
          <span className="stage-counter">Stage {currentStage}/{totalStages}</span>
        )}
      </div>
      
      {!isCompleted && (
        <>
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          
          {currentStageTitle && (
            <div className="current-stage-title">
              {currentStageTitle}
            </div>
          )}
        </>
      )}

      {/* Stage dots for visual progress */}
      {stages && stages.length > 0 && (
        <div className="stage-dots">
          {stages.map((stage, idx) => (
            <div 
              key={idx}
              className={`stage-dot ${stage.status}`}
              title={stage.title}
            >
              {stage.status === 'completed' ? '✓' : idx + 1}
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .framework-progress-badge {
          background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
          border: 1px solid #86efac;
          border-radius: 12px;
          padding: 12px 16px;
          margin: 8px 0;
          font-size: 13px;
        }

        .framework-progress-badge.completed {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border-color: #22c55e;
        }

        .progress-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .framework-icon {
          font-size: 16px;
        }

        .framework-name {
          font-weight: 600;
          color: #166534;
          flex: 1;
        }

        .stage-counter {
          background: #22c55e;
          color: white;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }

        .completed-badge {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
          padding: 3px 10px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          animation: pulse 1s ease infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .progress-bar-container {
          height: 6px;
          background: #d1fae5;
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #22c55e 0%, #10b981 100%);
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        .current-stage-title {
          color: #15803d;
          font-size: 12px;
          font-style: italic;
        }

        .stage-dots {
          display: flex;
          gap: 6px;
          margin-top: 10px;
          justify-content: center;
        }

        .stage-dot {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .stage-dot.completed {
          background: #22c55e;
          color: white;
        }

        .stage-dot.current {
          background: #fef3c7;
          border: 2px solid #f59e0b;
          color: #92400e;
          animation: currentPulse 1.5s ease infinite;
        }

        @keyframes currentPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
          50% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
        }

        .stage-dot.pending {
          background: #e2e8f0;
          color: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default FrameworkProgressBadge;
